import { cn } from '@/lib/utils';
import { MANDATE_THRESHOLD_PASS } from '@/config/indexThresholds';

const COMMERCIAL_THRESHOLD = 70;

type MetricCardProps = {
  value: number | null | undefined;
  label: string;
  sublabel: string;
  sublabelClassName?: string;
};

function MetricCard({ value, label, sublabel, sublabelClassName }: MetricCardProps) {
  return (
    <div className="flex flex-col items-center text-center min-w-[7rem]">
      <p className="text-4xl md:text-5xl font-sans font-bold tabular-nums text-white">
        {value != null && !Number.isNaN(value) ? `${Math.round(value)}%` : '—'}
      </p>
      <p className="mt-2 text-[10px] font-mono uppercase tracking-[0.2em] text-white/45">{label}</p>
      <p
        className={cn(
          'text-[10px] font-mono uppercase tracking-wider mt-1',
          sublabelClassName ?? 'text-index-rewrite',
        )}
      >
        {sublabel}
      </p>
    </div>
  );
}

type RecommendationHeadlineMetricsProps = {
  engagementPct?: number | null;
  commercialityPct?: number | null;
  mandateScore?: number | null;
  recommendationLabel?: string | null;
};

export function RecommendationHeadlineMetrics({
  engagementPct,
  commercialityPct,
  mandateScore,
  recommendationLabel,
}: RecommendationHeadlineMetricsProps) {
  const engagementSublabel =
    recommendationLabel?.trim().toUpperCase() || '—';

  const commercialSublabel =
    commercialityPct != null && !Number.isNaN(commercialityPct)
      ? commercialityPct >= COMMERCIAL_THRESHOLD
        ? 'Above 70% threshold'
        : `Below ${COMMERCIAL_THRESHOLD}% threshold`
      : '—';

  const mandateSublabel =
    mandateScore == null || Number.isNaN(mandateScore)
      ? 'Not applicable'
      : mandateScore >= MANDATE_THRESHOLD_PASS
        ? 'Mandate pass'
        : `Below ${MANDATE_THRESHOLD_PASS}% threshold`;

  const mandateSublabelClass =
    mandateScore != null &&
    !Number.isNaN(mandateScore) &&
    mandateScore >= MANDATE_THRESHOLD_PASS
      ? 'text-index-cleared'
      : 'text-index-rewrite';

  return (
    <div className="flex flex-wrap items-start justify-center gap-8 md:gap-12 py-4 mb-6 border-b border-[#2a2a2a]">
      <MetricCard
        value={engagementPct}
        label="Engagement"
        sublabel={engagementSublabel}
        sublabelClassName="text-white/55"
      />
      <MetricCard
        value={commercialityPct}
        label="Commerciality"
        sublabel={commercialSublabel}
        sublabelClassName={
          commercialityPct != null &&
          !Number.isNaN(commercialityPct) &&
          commercialityPct >= COMMERCIAL_THRESHOLD
            ? 'text-index-cleared'
            : 'text-index-rewrite'
        }
      />
      <MetricCard
        value={mandateScore}
        label="Adherence to Mandate"
        sublabel={mandateSublabel}
        sublabelClassName={mandateSublabelClass}
      />
    </div>
  );
}

/** Strip legacy recommendation markdown (Dear line + inline metric bold lines). */
export function stripLegacyRecommendationMetrics(markdown: string): string {
  if (!markdown?.trim()) return markdown;
  return markdown
    .replace(/^Dear .+?, consider:\s*\n+/im, '')
    .replace(/^\*\*Engagement = .+\*\*\s*\n+/im, '')
    .replace(/^\*\*Commerciality = .+\*\*\s*\n+/im, '')
    .replace(/^\*\*Adherence to Mandate = .+\*\*\s*\n+/im, '')
    .replace(/\n{3,}/g, '\n\n');
}

export default RecommendationHeadlineMetrics;
